import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  GridColDef,
  GridFilterModel,
  GridToolbarFilterButton,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import {
  DocumentGenerationTemplate,
  DocumentGenerationTemplate_FieldsFragment,
  DocumentGenerationTemplate_TemplatesDataGridPageFilter,
  TemplatesDataGridColumns,
  useDocumentGenerationTemplate_GetAllDocumentGenerationTemplatesForTemplatesDataGridLazyQuery,
} from 'queries';
import { ChevronRight } from 'ts-react-feather-icons';

import { ServerEnabledDataGrid } from 'common/server-enabled-data-grid';
import Spinner from 'common/spinner';
import HomeLayout from 'layouts/home-layout';
import DocumentTagPill from 'pages/common/document/document-tag-pill';
import { GridStateKey } from 'utils/grid-state';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';
import { defaultPaginationModel } from 'utils/pagination-model';

import CreateDocumentGenerationTemplateModal from './components/create-document-generation-template-modal';
import DocumentGenerationTemplateActions from './components/document-generation-template-actions';
import EditDocumentGenerationTemplateModal from './components/edit-document-generation-template-modal';

const useStyles = makeStyles(() => ({
  root: {
    // no border around the cells when focused
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
  },
  button: { marginBottom: '10px' },
  container: {
    padding: '5%',
    flexGrow: 1,
  },
  title: {
    fontWeight: 600,
  },
  routeIconContainer: {
    verticalAlign: 'middle',
  },
  link: {
    cursor: 'pointer',
  },
  header: {
    marginRight: '10px',
  },
}));

export default function ManageDocumentGenerationTemplates() {
  const classes = useStyles();
  const history = useHistory();

  const [selectedDocumentGenerationTemplate, setSelectedDocumentGenerationTemplate] = useState<
    DocumentGenerationTemplate | undefined
  >(undefined);

  const [showEditDocumentGenerationTemplateModal, setShowEditDocumentGenerationTemplateModal] =
    useState<boolean>(false);

  const [showCreateDocumentGenerationTemplateModal, setShowCreateDocumentGenerationTemplateModal] =
    useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [baseFilterModelFromDataGridTab, setBaseFilterModelFromDataGridTab] =
    useState<GridFilterModel>({
      items: [],
    });

  const gridApiRef = useGridApiRef();

  const [
    getAllDocumentGenerationTemplatesForTemplatesDataGridLazyQuery,
    getAllDocumentGenerationTemplatesForTemplatesDataGridLazyQueryResult,
  ] = useDocumentGenerationTemplate_GetAllDocumentGenerationTemplatesForTemplatesDataGridLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: false,
    variables: {
      input: {
        paginationInput: {
          page: defaultPaginationModel.page,
          pageSize: defaultPaginationModel.pageSize,
        },
      },
    },
    onError: (e) => {
      showAlert({
        message:
          'Error getting all document generation templates for templates data grid: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const columns: GridColDef<DocumentGenerationTemplate_FieldsFragment>[] = [
    {
      disableColumnMenu: true,
      field: TemplatesDataGridColumns.Name,
      filterable: true,
      flex: 2,
      headerName: 'Name',
      renderCell: ({ row }) => {
        return row.name;
      },
      valueGetter: ({ row }) => row.name,
    },
    {
      disableColumnMenu: true,
      field: TemplatesDataGridColumns.Type,
      filterable: false,
      flex: 2,
      headerName: 'Type',
      renderCell: ({ row }) => {
        return row.type;
      },
      valueGetter: ({ row }) => row.type,
    },
    {
      disableColumnMenu: true,
      field: TemplatesDataGridColumns.ExternalId,
      filterable: true,
      flex: 2,
      headerName: 'External ID',
      renderCell: ({ row }) => {
        return row.externalDocumentIdentifier;
      },
      valueGetter: ({ row }) => row.externalDocumentIdentifier,
    },
    {
      disableColumnMenu: true,
      field: TemplatesDataGridColumns.UploadPath,
      filterable: false,
      flex: 2,
      headerName: 'Upload Path',
      renderCell: ({ row }) => {
        return row.uploadPath;
      },
      valueGetter: ({ row }) => row.uploadPath,
    },
    {
      disableColumnMenu: true,
      field: TemplatesDataGridColumns.DocumentTagName,
      filterable: true,
      flex: 2,
      headerName: 'Document Tag',
      renderCell: ({ row }) => {
        return <DocumentTagPill documentTag={row.documentTag} />;
      },
      valueGetter: ({ row }) => row.documentTag.name,
    },
    {
      disableColumnMenu: true,
      field: TemplatesDataGridColumns.SignatureType,
      filterable: false,
      flex: 2,
      headerName: 'Signature Type',
      renderCell: ({ row }) => {
        return row.signatureType || 'N/A';
      },
      valueGetter: ({ row }) => row.signatureType || 'N/A',
    },
    {
      disableColumnMenu: true,
      field: TemplatesDataGridColumns.SkipPrinting,
      filterable: false,
      flex: 2,
      headerName: 'Skip Printing',
      renderCell: ({ row }) => {
        return row.skipPrinting ? 'Yes' : 'No';
      },
      valueGetter: ({ row }) => (row.skipPrinting ? 'Yes' : 'No'),
    },
    {
      disableColumnMenu: true,
      field: TemplatesDataGridColumns.Instructions,
      filterable: false,
      flex: 2,
      headerName: 'Instructions',
      renderCell: ({ row }) => {
        return row.instructions;
      },
      valueGetter: ({ row }) => row.instructions,
    },
    {
      disableColumnMenu: true,
      field: 'actions',
      filterable: false,
      flex: 2,
      headerName: 'Actions',
      renderCell: ({ row }) => {
        return (
          <DocumentGenerationTemplateActions
            documentGenerationTemplate={row}
            showEditDocumentGenerationTemplateModal={setShowEditDocumentGenerationTemplateModal}
            setSelectedDocumentGenerationTemplate={setSelectedDocumentGenerationTemplate}
          />
        );
      },
    },
  ];

  const getDocumentGenerationTemplateGridToolbar = () => {
    return (
      <div>
        <GridToolbarFilterButton />
      </div>
    );
  };

  if (getAllDocumentGenerationTemplatesForTemplatesDataGridLazyQueryResult.loading) {
    return <Spinner />;
  }

  return (
    <HomeLayout showPortalName={false}>
      <>
        {showEditDocumentGenerationTemplateModal && selectedDocumentGenerationTemplate && (
          <EditDocumentGenerationTemplateModal
            showModal={showEditDocumentGenerationTemplateModal}
            setShowModal={setShowEditDocumentGenerationTemplateModal}
            setSelectedDocumentGenerationTemplate={setSelectedDocumentGenerationTemplate}
            selectedDocumentGenerationTemplate={selectedDocumentGenerationTemplate}
          />
        )}
        {showCreateDocumentGenerationTemplateModal && (
          <CreateDocumentGenerationTemplateModal
            showModal={showCreateDocumentGenerationTemplateModal}
            setShowModal={setShowCreateDocumentGenerationTemplateModal}
          />
        )}
        <div className={classes.container}>
          <div style={{ display: 'flex', verticalAlign: 'bottom' }}>
            <Typography className={classes.title} variant="h5" style={{ flex: 2 }}>
              <span
                onClick={() => {
                  history.push(`/home`);
                }}
              >
                <span className={classes.routeIconContainer}>
                  <img src="/home-icon.svg" alt="home-icon" />
                </span>
                <span className={classes.routeIconContainer}>
                  <ChevronRight />
                </span>
              </span>
              Templates
            </Typography>
            <div className={classes.button}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                style={{ marginTop: '3px', padding: '2px 10px' }}
                onClick={() => {
                  setShowCreateDocumentGenerationTemplateModal(true);
                }}
              >
                + Create New Template
              </Button>
            </div>
          </div>
          <Grid container spacing={2}>
            <Grid item md={12} xs={12}>
              <ServerEnabledDataGrid<DocumentGenerationTemplate_TemplatesDataGridPageFilter>
                apiRef={gridApiRef}
                gridStateKey={GridStateKey.DocumentGenerationTemplateGrid}
                className={classes.root}
                loading={
                  getAllDocumentGenerationTemplatesForTemplatesDataGridLazyQueryResult.loading
                }
                columns={columns}
                baseFilterModelFromDataGridTab={baseFilterModelFromDataGridTab}
                queryResult={getAllDocumentGenerationTemplatesForTemplatesDataGridLazyQueryResult}
                executeLazyQuery={getAllDocumentGenerationTemplatesForTemplatesDataGridLazyQuery}
                disableColumnReorder
                disableRowSelectionOnClick
                autoHeight={true}
                rowHeight={65}
                slots={{
                  toolbar: getDocumentGenerationTemplateGridToolbar,
                }}
              />
            </Grid>
          </Grid>
        </div>
      </>
    </HomeLayout>
  );
}
